import React, { useEffect, useState } from 'react';
import { Loader2, Plus, Send, Trash2 } from 'lucide-react';
import { formConfigAPI } from '../../services/api';
import { toast } from '../ui/use-toast';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { apiError, loadFormMeta, type FormMeta, type LangMap } from './formMeta';

/**
 * 2026-09-26: the WhatsApp / Automation side of a pre-chat form
 * (Docs/RELEASE_NOTES_2026-09-24_FORMS.md). Forms live in pre_chat_forms;
 * these settings are stored in the form's `options`, the account-wide
 * behaviour in accounts.feature_flags["forms"]. Every list offered here
 * (kinds, collectors, repeat policies) comes from GET /form-config/meta.
 */

// ---------------------------------------------------------------- translations

export const LangMapEditor: React.FC<{
  value: LangMap | null | undefined;
  onChange: (next: LangMap) => void;
  placeholder?: string;
  rows?: number;
}> = ({ value, onChange, placeholder, rows = 2 }) => {
  const entries = Object.entries(value && Object.keys(value).length ? value : { en: '' });
  const [newLang, setNewLang] = useState('');
  return (
    <div className="space-y-1.5">
      {entries.map(([lang, text]) => (
        <div key={lang} className="flex items-start gap-2">
          <Badge variant="outline" className="mt-1.5 w-10 justify-center font-mono text-[10px] uppercase">{lang}</Badge>
          <Textarea
            rows={rows}
            value={text}
            placeholder={placeholder}
            onChange={(e) => onChange({ ...(value || {}), [lang]: e.target.value })}
          />
          {lang !== 'en' && (
            <Button
              type="button" variant="ghost" size="sm" className="mt-1 h-7 w-7 p-0 text-muted-foreground"
              onClick={() => {
                const next = { ...(value || {}) };
                delete next[lang];
                onChange(next);
              }}
              aria-label={`Remove ${lang}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      ))}
      <div className="flex items-center gap-2">
        <Input
          className="h-7 w-28 text-xs"
          value={newLang}
          onChange={(e) => setNewLang(e.target.value.toLowerCase().slice(0, 10))}
          placeholder="Language, e.g. sw"
        />
        <Button
          type="button" variant="ghost" size="sm" className="h-7 text-xs"
          disabled={!newLang || entries.some(([l]) => l === newLang)}
          onClick={() => {
            onChange({ ...(value || {}), [newLang]: '' });
            setNewLang('');
          }}
        >
          <Plus className="mr-1 h-3 w-3" /> Add translation
        </Button>
      </div>
      <p className="text-[11px] text-muted-foreground">Languages you leave out are machine-translated from English (WhatsApp settings tab).</p>
    </div>
  );
};

// ---------------------------------------------------------------- per-form options
// 2026-09-29: the per-form settings (and the manual WhatsApp Flow ID fields)
// moved to pages/PreChatFormSettings.tsx; the only Flow is the one published
// from the form itself.

// ---------------------------------------------------------------- WhatsApp preview

export const FormWhatsAppPreview: React.FC<{
  form: { id: number; title: string } | null;
  onClose: () => void;
}> = ({ form, onClose }) => {
  const [lang, setLang] = useState('en');
  const [data, setData] = useState<any>(null);
  const [sending, setSending] = useState(false);
  useEffect(() => {
    if (!form) return;
    setData(null);
    formConfigAPI.previewForm(form.id, lang).then(setData).catch((err: any) =>
      toast({ title: 'Preview failed', description: apiError(err, ''), variant: 'destructive' }));
  }, [form, lang]);
  const testSend = async () => {
    if (!form) return;
    setSending(true);
    try {
      const r = await formConfigAPI.testSendForm(form.id);
      toast({
        title: r.delivered ? 'Sent to your WhatsApp' : 'Could not send',
        description: r.delivered ? 'Answer it on your phone to try the whole form.' : r.result?.error,
        variant: r.delivered ? undefined : 'destructive',
      });
    } catch (err: any) {
      toast({ title: 'Could not send', description: apiError(err, ''), variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };
  return (
    <Dialog open={!!form} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-md overflow-y-auto">
        <DialogHeader>
          <DialogTitle>On WhatsApp · {form?.title}</DialogTitle>
          <DialogDescription>What a contact sees in chat, one question at a time. Nothing is sent.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2">
          <Input className="h-8 w-32" value={lang} onChange={(e) => setLang(e.target.value.toLowerCase())} placeholder="language" />
          <Button size="sm" variant="outline" className="ml-auto gap-1.5" disabled={sending} onClick={testSend}>
            {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send to my WhatsApp
          </Button>
        </div>
        {!data ? (
          <Loader2 className="mx-auto my-6 h-5 w-5 animate-spin text-muted-foreground" />
        ) : (
          <div className="space-y-3 rounded-lg bg-muted/40 p-3">
            {data.messages.map((m: any) => (
              <div key={m.field_key} className="max-w-[85%] rounded-lg bg-background p-2.5 text-sm shadow-sm">
                <div className="whitespace-pre-wrap">{m.text}</div>
                {(m.buttons || m.rows) && (
                  <div className="mt-2 flex flex-wrap gap-1 border-t pt-2">
                    {(m.buttons || m.rows).map((b: any) => (
                      <span key={b.id} className="rounded-full border px-2 py-0.5 text-xs text-sky-700 dark:text-sky-400">{b.title}</span>
                    ))}
                  </div>
                )}
                {(m.conditions || []).length > 0 && (
                  <div className="mt-1 text-[11px] text-muted-foreground">
                    {m.conditions.map((c: any) => `${c.action} if ${c.field_id} ${c.operator}${c.value !== undefined && c.value !== null ? ` ${c.value}` : ''}`).join(' · ')}
                  </div>
                )}
              </div>
            ))}
            {data.completion && <div className="max-w-[85%] rounded-lg bg-background p-2.5 text-sm shadow-sm">{data.completion}</div>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ---------------------------------------------------------------- account settings

const WORD_LISTS: Array<[string, string, string]> = [
  ['decline_words', 'Means "no, I don\'t want to answer"', 'Ends the form (the held step continues).'],
  ['skip_words', 'Means "skip this question"', 'Skips an optional question; on a required one it counts as declining.'],
  ['cancel_words', 'Means "stop the form"', ''],
  ['yes_words', 'Counts as Yes', 'For Yes / No (checkbox) questions.'],
  ['no_words', 'Counts as No', 'For Yes / No (checkbox) questions.'],
  ['human_request_words', 'Extra "talk to a human" phrases', 'Cancels any form in progress and lets the message reach normal escalation.'],
];

export const FormsSettingsPanel: React.FC = () => {
  const [meta, setMeta] = useState<FormMeta | null>(null);
  const [s, setS] = useState<Record<string, any> | null>(null);
  const [saving, setSaving] = useState(false);
  const [lang, setLang] = useState('en');
  useEffect(() => {
    Promise.all([loadFormMeta(), formConfigAPI.getSettings()])
      .then(([m, r]) => {
        setMeta(m);
        setS(r.effective_settings);
      })
      .catch((err: any) => toast({ title: 'Could not load WhatsApp settings', description: apiError(err, ''), variant: 'destructive' }));
  }, []);
  if (!meta || !s) return <Loader2 className="my-6 h-5 w-5 animate-spin text-muted-foreground" />;

  const set = (key: string, value: any) => setS((x) => ({ ...(x || {}), [key]: value }));
  const setNudge = (key: string, value: any) => setS((x) => ({ ...(x || {}), nudge_policy: { ...((x || {}).nudge_policy || {}), [key]: value } }));
  const messageKeys = Object.keys(meta.default_settings.messages?.en || {});
  const messages = (s.messages || {}) as Record<string, Record<string, string>>;
  const num = (v: string) => (v === '' ? 0 : Number(v));
  const save = async () => {
    setSaving(true);
    try {
      const r = await formConfigAPI.updateSettings(s);
      setS(r.effective_settings);
      toast({ title: 'Settings saved' });
    } catch (err: any) {
      toast({ title: 'Could not save', description: apiError(err, ''), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-6">
      <p className="text-sm text-muted-foreground">
        How every form behaves when it's asked on WhatsApp. Applies to all forms of this account.
      </p>
      <section className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        {[
          ['Give up after (hours)', s.timeout_hours, (v: string) => set('timeout_hours', num(v))],
          ['Re-asks after an invalid answer', s.reask_limit, (v: string) => set('reask_limit', num(v))],
          ['Max nudges per form', s.nudge_policy?.max_asks, (v: string) => setNudge('max_asks', num(v))],
          ['Hours between nudges', s.nudge_policy?.cooldown_hours, (v: string) => setNudge('cooldown_hours', num(v))],
          ['Close an ignored nudge after (messages)', s.nudge_policy?.cooldown_messages, (v: string) => setNudge('cooldown_messages', num(v))],
          ['Options sent as buttons (max)', s.max_buttons, (v: string) => set('max_buttons', num(v))],
          ['Options sent as a list (max)', s.max_list_rows, (v: string) => set('max_list_rows', num(v))],
        ].map(([label, value, onValue]: any) => (
          <div key={label} className="space-y-1.5">
            <Label>{label}</Label>
            <Input type="number" min={0} value={value ?? ''} onChange={(e) => onValue(e.target.value)} />
          </div>
        ))}
        <div className="space-y-1.5">
          <Label>Nudge placement</Label>
          <Select value={s.nudge_policy?.placement || 'append'} onValueChange={(v) => setNudge('placement', v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="append">Append to the AI reply</SelectItem>
              <SelectItem value="separate">Own message</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </section>

      <section className="space-y-2">
        {[
          ['translate_replies_for_matching', 'Translate replies to English before matching the word lists below'],
          ['auto_translate_prompts', 'Machine-translate questions into the contact\'s language when a translation is missing'],
          ['nudge_llm_answer_check', 'Use a quick AI check to tell whether a free-text reply answers a nudge'],
        ].map(([key, label]) => (
          <div key={key} className="flex items-center justify-between rounded-md border px-3 py-2">
            <span className="text-sm">{label}</span>
            <Switch checked={!!s[key]} onCheckedChange={(v) => set(key, v)} />
          </div>
        ))}
      </section>

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {WORD_LISTS.map(([key, label, hint]) => (
          <div key={key} className="space-y-1.5">
            <Label>{label}</Label>
            <Input
              value={(s[key] || []).join(', ')}
              onChange={(e) => set(key, e.target.value.split(',').map((w) => w.trim()).filter(Boolean))}
              placeholder="comma-separated"
            />
            {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
          </div>
        ))}
      </section>

      <section className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="font-semibold">Messages</h3>
            <p className="text-sm text-muted-foreground">
              Everything the bot says on its own. {'{question}'}, {'{form_name}'} and {'{message}'} are filled in when sent.
            </p>
          </div>
          <Input className="h-8 w-32" value={lang} onChange={(e) => setLang(e.target.value.toLowerCase().slice(0, 10))} placeholder="language" />
        </div>
        {messageKeys.map((key) => (
          <div key={key} className="space-y-1">
            <Label className="font-mono text-xs">{key}</Label>
            <Textarea
              rows={2}
              value={messages[lang]?.[key] ?? ''}
              placeholder={lang === 'en' ? '' : `${messages.en?.[key] || ''} (machine-translated if empty)`}
              onChange={(e) => set('messages', { ...messages, [lang]: { ...(messages[lang] || {}), [key]: e.target.value } })}
            />
          </div>
        ))}
      </section>

      <div className="flex gap-2">
        <Button disabled={saving} onClick={save}>
          {saving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Save settings
        </Button>
        <Button variant="outline" onClick={() => setS(meta.default_settings)}>Reset to defaults</Button>
      </div>
    </div>
  );
};
